import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CreateSupportTicketRequestSchema,
  SUPPORT_ATTACHMENT_ACCEPT,
  SUPPORT_ATTACHMENT_MAX_BYTES,
  SUPPORT_ATTACHMENT_MAX_COUNT,
  type CreateSupportTicketRequest
} from '@bliks-io/contracts';
import { FormInput } from '@/components/form/rhf/FormInput';
import { FormSelect } from '@/components/form/rhf/FormSelect';
import { FormTextarea } from '@/components/form/rhf/FormTextarea';
import { Button, DropZone } from '@bliks-io/ui';
import { useCreateSupportTicket } from '@/api/mutations/useSupport';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { CheckCircle2 } from 'lucide-react';
import { AttachmentList } from './AttachmentList';

const MAX_ATTACHMENT_MB = SUPPORT_ATTACHMENT_MAX_BYTES / 1024 / 1024;

export function ContactForm() {
  const { t } = useTranslation();
  const mutation = useCreateSupportTicket();
  const [attachments, setAttachments] = useState<File[]>([]);

  const { control, handleSubmit, reset } = useForm<CreateSupportTicketRequest>({
    resolver: zodResolver(CreateSupportTicketRequestSchema),
    defaultValues: {
      category: undefined,
      summary: '',
      description: ''
    }
  });

  const onSubmit = (data: CreateSupportTicketRequest) => {
    mutation.mutate(
      {
        ...data,
        browser: navigator.userAgent,
        appVersion: __APP_VERSION__ || undefined,
        attachments
      },
      {
        onSuccess: () => {
          reset();
          setAttachments([]);
        },
        onError: (error) => toastApiError(error, 'helpCenter.contact.errorMessage')
      }
    );
  };

  const addAttachments = (files: File[]) => {
    setAttachments((current) => [...current, ...files].slice(0, SUPPORT_ATTACHMENT_MAX_COUNT));
  };

  const removeAttachment = (index: number) => {
    setAttachments((current) => current.filter((_, position) => position !== index));
  };

  // Static keys (not a dynamic t(`...${value}`) template) so check:translations
  // can resolve every category label; the values mirror SupportCategorySchema.
  const categoryOptions = [
    { value: 'incident', label: t('helpCenter.contact.categories.incident') },
    { value: 'request', label: t('helpCenter.contact.categories.request') },
    { value: 'change', label: t('helpCenter.contact.categories.change') },
    { value: 'complaint', label: t('helpCenter.contact.categories.complaint') }
  ];

  if (mutation.isSuccess) {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <CheckCircle2 className="h-10 w-10 text-positive" />
        <p className="text-center font-medium">{t('helpCenter.contact.successTitle')}</p>
        <p className="text-center text-sm text-muted-foreground">
          {t('helpCenter.contact.successMessage')}
        </p>
        {mutation.data?.ticketNumber && (
          <p className="text-center text-sm font-medium">
            {t('helpCenter.contact.ticketReference', { number: mutation.data.ticketNumber })}
          </p>
        )}
        <Button variant="outline" size="small" onClick={() => mutation.reset()}>
          {t('helpCenter.contact.newRequest')}
        </Button>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <FormSelect
        control={control}
        name="category"
        label={t('helpCenter.contact.category')}
        options={categoryOptions}
        placeholder={t('helpCenter.contact.categoryPlaceholder')}
        required
      />

      <FormInput
        control={control}
        name="summary"
        label={t('helpCenter.contact.summary')}
        placeholder={t('helpCenter.contact.summaryPlaceholder')}
        required
      />

      <FormTextarea
        control={control}
        name="description"
        label={t('helpCenter.contact.description')}
        placeholder={t('helpCenter.contact.descriptionPlaceholder')}
        rows={5}
        required
      />

      <div className="space-y-2">
        {attachments.length < SUPPORT_ATTACHMENT_MAX_COUNT && (
          <DropZone
            id="support-attachments"
            onChange={addAttachments}
            accept={SUPPORT_ATTACHMENT_ACCEPT}
            multiple
            maxSize={MAX_ATTACHMENT_MB}
            disabled={mutation.isPending}
            title={t('helpCenter.contact.attachmentsTitle')}
            description={t('helpCenter.contact.attachmentsHint', {
              count: SUPPORT_ATTACHMENT_MAX_COUNT,
              size: MAX_ATTACHMENT_MB
            })}
          />
        )}

        <AttachmentList files={attachments} onRemove={removeAttachment} />
      </div>

      <Button type="submit" size="medium" loading={mutation.isPending}>
        {t('helpCenter.contact.submit')}
      </Button>
    </form>
  );
}
